/**
 * `Entities_Registry.ts` — port of `AI/Registry/Entities_Registry.cs`.
 *
 * Descriptions are verbatim Unity copy. Web slice: `move_relative` is
 * deferred, so it is not registered. `constant_spin`/`clear_constant_spin` are
 * live (M9 spin port — snapshot `PersistentSpinJson` + spin playback runtime),
 * as are `highlight_part`/`clear_highlight` (snapshot `PersistentHighlightJson`
 * + the outline pass in `gizmos/SelectionOutlineController`; a card's
 * `MediaTileData.persistentHighlightJson` + its own frame).
 *
 * `look_at` is registered without Unity's `scriptUid` / legacy `actionUid`
 * (script buttons are not ported — the Scripts model is deprioritized), so
 * its description drops the script-button sentences and keeps the rest
 * verbatim. Parts, selection sets and text boxes turn the camera in place
 * (`ISceneModel.transitionCameraLookAt`), as upstream.
 */

import { AgentToolNames } from "./agentToolNames";
import {
  type AIAgentTool,
  boolParam,
  buildParams,
  numberParam,
  numberList,
  stringEnum,
  stringList,
  stringParam,
} from "./AIAgentToolRegistryModel";

export function addEntitiesTools(tools: AIAgentTool[]): void {
  tools.push(
    {
      name: AgentToolNames.MoveParts,
      category: "Authoring",
      description: `Moves parts, selection sets, or the top-level model by changing position (meters), rotation (Euler degrees), and/or scale. Axis: X=right, Y=up, Z=forward, in scene space (the app's stable scene-space root, not Unity's raw world). Convert non-metric inputs to meters. Use this for one-time finite rotations including 'rotate once'. Resolve targets first (see system prompt) and pass explicit identifiers via partPathsToMove or selectionSetNameToMove. CRITICAL scale semantics: with useIncrement=true (the DEFAULT), scale is a component-wise MULTIPLIER on the current scale, not an additive delta. So 'make it 50% larger' -> scale=[1.5,1.5,1.5]; 'make it 50% smaller' -> scale=[0.5,0.5,0.5]; 'double its size' -> scale=[2,2,2]; 'half size' -> scale=[0.5,0.5,0.5]; 'no change' is [1,1,1]. NEVER pass scale=[0,0,0] (collapses the part) for 'no change'. Use useIncrement=false ONLY when the user gives an explicit absolute target scale (e.g. 'set scale to 2'). Position and rotation with useIncrement=true remain ADDITIVE deltas (e.g. 'rotate 30 deg more' -> rotation=[0,30,0]; 'move 10 cm right' -> position=[0.1,0,0]). For position/rotation adjustments relative to current values where you need the existing exact value (e.g. 'set rotation back to its original Y'), call ${AgentToolNames.GetPartTransforms} first, compute the absolute, and pass with useIncrement=false. See system prompt for transition defaults, scene-space rules, opposite-direction handling, and 'only include the transform fields the user asked to change'.`,
      humanDescription:
        "Ask AI to move, rotate, or scale parts, selection sets, or whole models, using local or scene space, relative or absolute changes, and optional animated transitions.",
      parameters: buildParams({
        position: numberList(
          "[x, y, z] meters in scene space (X right, Y up, Z forward). Only specify when changing position.",
        ),
        rotation: numberList(
          "[x, y, z] Euler degrees in scene space. Only specify when changing rotation.",
        ),
        scale: numberList(
          "[x, y, z] scale. With useIncrement=true (default) this is a MULTIPLIER on the current scale: [1.5,1.5,1.5]=50% larger, [0.5,0.5,0.5]=50% smaller, [2,2,2]=double, [1,1,1]=no change. With useIncrement=false this is the absolute scale ([1,1,1]=nominal). Only specify when changing scale.",
        ),
        useIncrement: boolParam(
          "True (default) = position/rotation are additive deltas; scale is a multiplier on current scale. False = treat all values as absolute. Default true.",
          true,
        ),
        isLocal: boolParam(
          "Only set true when the user explicitly asks for local/object/top-level-model space. Default false (scene space).",
          false,
        ),
        useTransition: boolParam(
          "Animate the transform. Default true; set false only when the user wants instant.",
          true,
        ),
        transitionDuration: numberParam(
          "Animation duration in seconds. Only set when the user specifies a duration; otherwise the 2 s default is used.",
        ),
        selectionSetNameToMove: stringParam("Exact selection set name from Get_Scene_Context."),
        partPathsToMove: stringList("Hierarchy paths from Get_Scene_Context (e.g. '0,1,2')."),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.HideParts,
      category: "Authoring",
      description:
        "Hides parts, text boxes, action buttons, or the top-level model. Resolve targets first (see system prompt). Pass identifiers via partPathsToHide / selectionSetNamesToHide / textBoxNamesToHide / actionButtonNamesToHide. Set hideAllParts=true to hide everything in the scene.",
      humanDescription:
        "Hide specific parts, text boxes, action buttons, groups, or the whole model to simplify the view.",
      parameters: buildParams({
        selectionSetNamesToHide: stringList("Exact selection set names from Get_Scene_Context."),
        textBoxNamesToHide: stringList("Exact text box names from Get_Scene_Context."),
        actionButtonNamesToHide: stringList("Exact action button names from Get_Scene_Context."),
        partPathsToHide: stringList("Hierarchy paths from Get_Scene_Context (e.g. '0,1,2')."),
        hideAllParts: boolParam(
          "Set true to hide every part in the active scene. Default false.",
          false,
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.UnhideParts,
      category: "Authoring",
      description:
        "Unhides parts, text boxes, action buttons, or the top-level model. Resolve targets first (see system prompt). Pass identifiers via partPathsToUnhide / selectionSetNamesToUnhide / textBoxNamesToUnhide / actionButtonNamesToUnhide. Set unhideAllParts=true to unhide everything.",
      humanDescription:
        "Show hidden parts, text boxes, or action buttons again, including specific targets or the full model.",
      parameters: buildParams({
        selectionSetNamesToUnhide: stringList("Exact selection set names from Get_Scene_Context."),
        textBoxNamesToUnhide: stringList("Exact text box names from Get_Scene_Context."),
        actionButtonNamesToUnhide: stringList(
          "Exact action button names from Get_Scene_Context.",
        ),
        partPathsToUnhide: stringList("Hierarchy paths from Get_Scene_Context (e.g. '0,1,2')."),
        unhideAllParts: boolParam(
          "Set true to unhide every part in the active scene. Default false.",
          false,
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.Isolate,
      category: "Authoring",
      description: `Fades all other parts to semi-transparent except the targets. Use ONLY when the user explicitly asks to fade everything else / isolate without moving the camera. For 'show me X' or 'find X' use ${AgentToolNames.FocusOnPart} with useIsolate=true instead so the camera also frames the part. Resolve targets first (see system prompt) and pass at least one of partPathsToIsolate or selectionSetNamesToIsolate.`,
      humanDescription:
        "Fade everything except the parts you want to focus on so the selected components stand out.",
      parameters: buildParams({
        selectionSetNamesToIsolate: stringList(
          "Exact selection set names from Get_Scene_Context.",
        ),
        partPathsToIsolate: stringList("Hierarchy paths from Get_Scene_Context (e.g. '0,1,2')."),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.ClearIsolation,
      category: "Authoring",
      description:
        "Clears the current isolation mode and restores normal visibility. Use this when the user wants to exit isolation mode or show all parts again.",
      humanDescription: "Return the full scene to normal visibility after isolating parts.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.ResetPlacement,
      category: "Authoring",
      description:
        "Hard-resets position and rotation of parts to their initial imported/default placement (same as the inspector reset button). Resolve targets first (see system prompt). For 'reset all' / 'reset everything', call Get_Scene_Context first and include all paths marked '(moved)' in partPathsToReset so only displaced parts are reset.",
      humanDescription:
        "Restore selected moved parts to their initial imported/default placement.",
      parameters: buildParams({
        selectionSetNameToReset: stringParam("Exact selection set name from Get_Scene_Context."),
        partPathsToReset: stringList(
          "Hierarchy paths from Get_Scene_Context. For 'reset all', include all '(moved)' paths.",
        ),
        useTransition: boolParam("Animate the reset. Default true.", true),
        transitionDuration: numberParam(
          "Animation duration in seconds. Defaults to 1 s when omitted.",
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.ResetScene,
      category: "Authoring",
      description:
        "Runs Campfire's scene reset: unexplodes any exploded models, then hard-resets the scene by restoring all objects' positions and rotations to their original default placement, even if gizmo edits were already persisted. Use when the user asks to reset the scene or restore everything to the original placement.",
      humanDescription:
        "Unexplode models, then hard reset the full scene back to its original placement.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.SelectPart,
      category: "Authoring",
      description: `Selects parts, selection sets, or the top-level model. Use ONLY when the user explicitly asks to select. For find/locate/show use ${AgentToolNames.FocusOnPart} instead. Resolve targets first (see system prompt). When a target name matches a selection set name, prefer selectionSetNameToSelect.`,
      humanDescription:
        "Select parts, models, or selection sets so follow-up AI commands can act on them.",
      parameters: buildParams({
        selectionSetNameToSelect: stringParam("Exact selection set name from Get_Scene_Context."),
        partPathsToSelect: stringList("Hierarchy paths from Get_Scene_Context (e.g. '0,1,2')."),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.FocusOnPart,
      category: "Authoring",
      description:
        "Frames the user's camera on a part (same as pressing F on a selection). Use for 'focus/frame', 'where is X / find X' (omit useIsolate), and 'show me X' (set useIsolate=true so other parts also fade). Resolve targets first (see system prompt).",
      humanDescription:
        "Move the camera to frame a part or model, with an option to isolate it at the same time.",
      parameters: buildParams({
        selectionSetNamesToFocus: stringList("Exact selection set names from Get_Scene_Context."),
        partPathsToFocus: stringList("Hierarchy paths from Get_Scene_Context (e.g. '0,1,2')."),
        useIsolate: boolParam(
          "Also fade other parts to semi-transparent. Set true for 'show me X'. Default false.",
        ),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.LookAtPart,
      category: "Authoring",
      description: `Rotates the current camera to face a part, model, or text label without changing camera position (same smooth transition Campfire uses between saved scene views). Use for 'look at / turn toward / face / point at'. Resolve targets first (see system prompt). For find/locate/show use ${AgentToolNames.FocusOnPart} instead.`,
      humanDescription:
        "Turn the current camera to face a part or label without changing distance.",
      parameters: buildParams({
        selectionSetNamesToLookAt: stringList("Exact selection set names from Get_Scene_Context."),
        textBoxNamesToLookAt: stringList("Exact text box names from Get_Scene_Context."),
        partPathsToLookAt: stringList("Hierarchy paths from Get_Scene_Context (e.g. '0,1,2')."),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.HighlightPart,
      category: "Authoring",
      description: `Applies a persistent highlight (default: a crisp yellow line on the part's edge with a soft halo just outside it, steady — pulsing is opt-in via pulseMode) to one or more parts or text boxes WITHOUT selecting them. Use ONLY for explicit 'highlight / outline / pulse / mark / call attention' requests. For 'make it red / change color / metallic / wood' use the material tools, NOT this. Resolve targets first (see system prompt). Persists until ${AgentToolNames.ClearHighlight} unless pulseCount is provided (auto-clears after the final pulse). When updating only one aspect of an existing highlight ('make it static', 'make it thinner'), preserve other current settings unless the user explicitly changes them.`,
      humanDescription:
        "Keep one or more scene targets persistently highlighted for emphasis without changing selection or materials.",
      parameters: buildParams({
        partPathsToHighlight: stringList(
          "Hierarchy paths from Get_Scene_Context (e.g. '0,1,2').",
        ),
        textBoxNamesToHighlight: stringList("Exact text box names from Get_Scene_Context."),
        semanticColor: stringEnum(
          "Standard meaning: attention=yellow/current focus, correct=green/completed, hazard=red/error or a required action. Default attention; use hazard only for errors and required actions, never as a generic highlight.",
          true,
          "attention",
          "correct",
          "hazard",
        ),
        colorHex: stringParam(
          "Custom hex color (e.g. #E8A33D amber, #0F99D4 info blue). Only when the user explicitly asks for a custom color.",
        ),
        pulseMode: stringEnum(
          "none (steady) is the default. Use continuous only when the user asks for a pulse or a moment needs to pull the eye; then omit pulseCount for infinite or provide pulseCount for a fixed count.",
          true,
          "continuous",
          "none",
        ),
        pulseCount: numberParam(
          "Positive whole number of pulse cycles when pulseMode=continuous (e.g. 1 for 'pulse once'). Omit for infinite. When provided, the highlight auto-clears after the final pulse.",
        ),
        thicknessMode: stringEnum(
          "Heaviest weight for the line and halo together; a part that is small on screen automatically draws a lighter one. Default thick.",
          true,
          "thin",
          "medium",
          "thick",
        ),
        outlineWidth: numberParam(
          "Line width in screen pixels, 3-16 (default 4, stepped down on a small part). Naming it pins the width and turns that step-down off. Only when the user asks for an exact width; otherwise use thicknessMode.",
        ),
        haloReach: numberParam(
          "How far the halo reaches OUT from the part's edge, in screen pixels, 3-18 (default 8, stepped down on a small part). Naming it pins the reach. Only when the user explicitly asks.",
        ),
        pulseMs: numberParam(
          "Length of one pulse in milliseconds, 1200-4000 (default 2800). Only when the user asks for a faster or slower pulse.",
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.ClearHighlight,
      category: "Authoring",
      description: `Clears persistent highlight(s) previously applied with ${AgentToolNames.HighlightPart}. Pass partPathsToClearHighlight or textBoxNamesToClearHighlight to clear specific targets, or omit all target identifiers to clear every persistent highlight in the active scene.`,
      humanDescription:
        "Remove persistent highlights from specific parts or clear all current persistent highlights.",
      parameters: buildParams({
        partPathsToClearHighlight: stringList(
          "Hierarchy paths from Get_Scene_Context (e.g. '0,1,2').",
        ),
        textBoxNamesToClearHighlight: stringList("Exact text box names from Get_Scene_Context."),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.ConstantSpinPart,
      category: "Authoring",
      description: `Applies persistent constant spin to one or more parts (default: parent/model up axis at 60 deg/s). Use for 'spin / rotate continuously / keep turning / slowly rotate'. Do NOT use for one-time finite rotations like 'rotate once' (use ${AgentToolNames.MoveParts} with rotation). Persists until ${AgentToolNames.ClearConstantSpin}. Resolve targets first (see system prompt). When updating only the speed, preserve the existing spin axis if the target is already spinning.`,
      humanDescription:
        "Keep one or more parts spinning continuously at a steady speed, with an optional x/y/z axis override.",
      parameters: buildParams({
        partPathsToSpin: stringList("Hierarchy paths from Get_Scene_Context (e.g. '0,1,2')."),
        spinAxis: stringEnum(
          "Parent/model axis: x=right, y=up, z=forward. Only set when user explicitly asks for a non-default axis. Default y.",
          true,
          "x",
          "y",
          "z",
        ),
        degreesPerSecond: numberParam(
          "Spin speed in deg/s. Default 60 for new spins. Only set when user specifies a speed or asks to speed up / slow down. Negative reverses direction.",
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.ClearConstantSpin,
      category: "Authoring",
      description: `Stops persistent spinning previously applied with ${AgentToolNames.ConstantSpinPart}. Pass partPathsToClearSpin to stop specific parts, or omit to stop every spinning part in the active scene.`,
      humanDescription:
        "Stop continuous spinning on specific parts or clear all current spinning parts.",
      parameters: buildParams({
        partPathsToClearSpin: stringList(
          "Hierarchy paths from Get_Scene_Context (e.g. '0,1,2').",
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.Explode,
      category: "Authoring",
      description:
        "Produces an exploded view of one or more whole models by separating their parts outward from the model origin, and is also used to collapse them back to fully assembled. Operates only on whole top-level models, not individual parts/selection sets/text labels: passing a part within a model auto-resolves to its root model. percentage is 0..100 (0=fully assembled, 100=max separation). Defaults to 50 when omitted. To unexplode/collapse/reassemble, pass percentage=0. Resolve targets first (see system prompt) via modelPathsToExplode (any part path within the model is OK). For generic 'the model' requests, call Get_Scene_Context to identify the root model; do not ask the user to select it first.",
      humanDescription:
        "Explode or unexplode whole models on a 0..100% scale (0 = assembled, 100 = max), defaulting to 50% when no percentage is given.",
      parameters: buildParams({
        percentage: numberParam("0..100. 0=assembled (unexplode), 100=max separation. Default 50."),
        modelPathsToExplode: stringList(
          "Hierarchy paths identifying target model(s) (e.g. '0' for the first root model, or any part path which is auto-resolved to its root model).",
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.UpdatePartContext,
      category: "Knowledge",
      description: `Updates the description/context for a specific part with new knowledge from the user. Explicit partPath/partName targets are authoritative; current selection is used only when no explicit target is supplied. Resolve targets first (see system prompt). newInformation REPLACES the existing description. If the user also tells you what the part should be called, ALSO call ${AgentToolNames.RenamePart} in the same step.`,
      humanDescription:
        "Save notes, explanations, or other knowledge for a specific part so AI can use it later.",
      parameters: buildParams({
        partName: stringParam(
          "Display name (only when no path is available). Takes precedence over current selection.",
        ),
        partPath: stringParam(
          "Hierarchy path from Get_Scene_Context (e.g. '0,1,2'). Takes precedence over current selection.",
        ),
        newInformation: stringParam(
          "Complete description that should replace the existing one.",
          false,
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.RenamePart,
      category: "Authoring",
      description: `Renames one or more parts by updating their display name. Explicit partPathsToRename/partPath targets are authoritative; current selection is used only when no explicit target is supplied. Use when the user says how a part should be called or asks to rename. Resolve targets first (see system prompt). If the user also gave knowledge/comments about the part, ALSO call ${AgentToolNames.UpdatePartContext} in the same step.`,
      humanDescription: "Rename one or more parts.",
      parameters: buildParams({
        partPathsToRename: stringList(
          "Hierarchy paths from Get_Scene_Context (e.g. '0,1,2'). Takes precedence over current selection.",
        ),
        partPath: stringParam(
          "Single hierarchy path (alternative to partPathsToRename). Takes precedence over current selection.",
        ),
        newDisplayName: stringParam("New display name applied to all targets.", false),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.ToggleModelAnimation,
      category: "Authoring",
      labelWhenActive: "Setting animation...",
      description:
        "Plays or stops a model's animation and sets how it plays - the same five controls as the inspector Animation panel. EVERY control is optional and each keeps its current value when omitted, so you can change one thing without restating the rest; pass at least one. 'enabled' is the play/stop switch - pass true to play, false to stop, and omit it to change how the animation plays without starting or stopping it. Setting what a model already has changes nothing, so enabled=true on an animation that has played to its end leaves it at the end - 'restart' is how to play it AGAIN from its first frame ('play that again', 'replay the animation'); it starts a stopped model too. 'take' picks which animation to play on a model that has more than one (Get_Scene_Context lists a model's takes after its assetId). 'speedPercent' is 100 for normal speed. 'direction' plays it forward or in reverse. 'playback' loops it, plays it once, or bounces it back and forth. Only works when the target model has animation. Resolve targets first (see system prompt) - pass partPath for any part inside the target model and the tool resolves to its root model.",
      humanDescription:
        "Play or stop a model's animation, and choose the take, speed, direction and looping.",
      parameters: buildParams({
        enabled: boolParam(
          "True to play the animation, false to stop it. Omit to leave it playing or stopped as it is and only change the settings below.",
        ),
        restart: boolParam(
          "True to play the animation again from its first frame - for 'play it again', 'replay the animation', 'from the start'. Plays a stopped model too; with take, replays that take from its start. During a guided run, with no partPath it replays the model the current step animates. Do not combine with enabled=false.",
        ),
        partPath: stringParam(
          "Hierarchy path of any part inside the target model (e.g. '0' for root model).",
        ),
        take: stringParam(
          "Which animation to play, by the name Get_Scene_Context lists for this model. Only set it when the user named an animation or the model has more than one.",
        ),
        speedPercent: numberParam(
          "Playback speed as a percentage - 100 is normal, 50 is half, 200 is double. Only set it when the user asked for a speed.",
        ),
        direction: stringEnum(
          "Which way the animation runs. Only set it when the user asked to reverse it.",
          true,
          "forward",
          "backward",
        ),
        playback: stringEnum(
          "How the animation repeats: loop forever, play once and stop at the end, or pingPong back and forth. Only set it when the user asked for one.",
          true,
          "loop",
          "once",
          "pingPong",
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.GetPartTransforms,
      category: "Context",
      description:
        "Returns transform info for a specific part by hierarchy path: localPosition, position, localRotation, rotation (Euler degrees), scale, isMoved (and originalPosition/originalRotation when isMoved=true). Use when you need transform values or to check whether a part has been displaced. Resolve targets first (see system prompt). "
        + `NOT A MEASURING TOOL - it returns PLACEMENT, never size. Scale is the factor a part was resized by, not a dimension, and position is measured to the part's origin from the scene's, not from the ground to the part. So it answers no question about how big, how wide, how long, how tall or how high above the ground anything is; ${AgentToolNames.GetPartDimensions} is the only source for those, and reaching here for one produces numbers that cannot be turned into the answer.`,
      humanDescription:
        "View a part's local/world position, rotation, scale, and whether it has moved from its original placement.",
      parameters: buildParams({
        partPath: stringParam("Hierarchy path from Get_Scene_Context (e.g. '0,1,2').", false),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.GetPartDimensions,
      category: "Context",
      labelWhenActive: "Measuring...",
      description:
        "Measures ONE part or model from its scene geometry and returns EITHER its size (width, height, depth) OR its height above the ground at three lines - bottomline (its lowest point), centerline (midway between its lowest and highest points) and topline (its highest point) - already formatted in the user's display units. This is the ONLY valid source for a size or a height - never state a dimension from a document or from memory, and never compute one yourself. "
        + "Use for every size question however it is phrased ('how big/tall/wide/long/deep is this', 'what are the dimensions of X'), AND for every ground-clearance question ('how high is X off the ground/floor', 'how far above the ground is X', 'how high is its centerline', 'how high is the top of it'). showGroundRuler picks WHICH of the two you are asking: leave it off and you get width/height/depth; set it true, or pass heightOf, and you get bottomlineHeightAboveGround, centerlineHeightAboveGround and toplineHeightAboveGround together. Speak only the line the question asks about - the bottom line for a plain 'how high is it off the ground'. You only ever receive the figures for the question you asked, so a follow-up that switches between size and height ALWAYS needs a fresh call - you will not have the other answer already. "
        + "CHECKING A REQUIREMENT. When the question is whether the part MEETS a requirement stated in a document passage you searched ('is it within spec', 'does it meet the standard', 'how does it compare to the spec'), pass the requirement as the document states it: requirementQuote, the requirement's exact words from the passage; requirementSubject, the words in that quote naming what it is about, which must be this part's name or one of its aliases; requirementProperty, the dimension it is about, since any dimension this tool measures can be checked; the figures exactly as written there - requirementMin and/or requirementMax, or requirementNominal with requirementTolerance - in requirementUnit, the unit the quote uses; and requirementDatum, the line the passage says a height above the ground is measured to, or unspecified when it does not say. The result's requirementCheck holds the verdict and every figure already in the requirement's unit: speak those, and never convert or compare anything yourself. The tool also reads every other passage of that search for this part, so you do not weigh them against each other: requirements lists each one behind the verdict. verdict within is a yes; outside is a no; conflicting_requirements (the documents state requirements that give different answers), depends_on_datum (the passage does not settle which figure it means and they disagree) and at_limit (a figure sits on a bound) mean you cannot tell. An error means the quote is not in any search result or the requirement is malformed: do not retry with a quote you composed - give the Not found reply. "
        + "NOT for empty space: an opening, gap or passage in a part, the headroom through it, or the distance between two parts. It measures a named part whole, so its figures describe the part around the space and answer none of those; do not call it for them unless a part itself carries that name. "
        + "NOT for weight or mass ('how heavy is it', 'what does it weigh') or for whether someone can lift, carry or pick it up - no geometry answers those; they are document facts, answered with Get_Part_Context. Call this only when your reply will state a figure it returns: it draws on the user's screen and labels the answer as measured, so a call made just in case puts up a box nobody asked for and marks a document's figure as measured. "
        + "Width/height/depth are measured along the part's own axes and match the labels drawn on the bounding box. bottomlineHeightAboveGround is the perpendicular distance from the ground plane to the LOWEST POINT OF THE PART itself; the ground is the datum, not a mat or platform the part rests on. centerlineHeightAboveGround is the distance from the same ground plane to the part's HORIZONTAL CENTERLINE, the level midway between its lowest and highest points - for a part lying level, such as a shaft or a roller, the height of its axis. toplineHeightAboveGround is the distance to the part's HIGHEST POINT. "
        + "THE PICTURE MUST MATCH THE QUESTION, AND THERE IS EXACTLY ONE PICTURE. A size question draws the bounding box, so the user can see which span each figure refers to. A clearance question (showGroundRuler=true) draws the ruler ALONE - no box, because the box's three spans are not what was asked and its labels would compete with the clearance figure. You do not choose between them: showGroundRuler picks the question and the picture follows. EVERY NEW QUESTION CLEARS THE PREVIOUS ANSWER'S PICTURE, so call again for every question you answer with a measurement - including a follow-up about the same part ('how long is it' then 'how high is it'). The figure and its illustration arrive together: call, and the user sees the picture for the question they just asked; skip the call and they see nothing at all. What is drawn stays until the user's next question, until they dismiss it, "
        + `${AgentToolNames.ClearBoundingBox} clears it, or the scene changes. `
        + "SILENT CALL - the call-again rule above is reasoning, not something to say: never announce that you are about to measure, explain that you are checking again, or narrate a comparison with an earlier answer. Call with nothing spoken, and speak once, with the figure. "
        + "Takes ONE resolved partPath: if the user's wording matches several parts, ask which one with present_options before calling.",
      humanDescription:
        "Measure a part's width, height and depth, or how far it sits above the ground, and show it in the scene.",
      parameters: buildParams({
        partPath: stringParam("Hierarchy path from Get_Scene_Context (e.g. '0,1,2').", false),
        showBoundingBox: boolParam(
          "Draw the bounding box around the part. Default true; only pass false when the user asked not to see it. Read for the SIZE question only - a clearance question (showGroundRuler=true) never draws a box, whatever this says.",
          true,
        ),
        showGroundRuler: boolParam(
          "Answer the CLEARANCE question rather than the size one: returns the three height lines instead of width/height/depth, and draws a ruler from the part's lowest point (or the line heightOf names) straight down to the ground labelled with that figure - the ruler INSTEAD OF the bounding box, not as well as it. TRUE only when the user asked about the part's distance from the ground or the floor ('how high is it off the ground', 'how far above the floor', 'what is its clearance'). FALSE for every question about the part's own size - 'how long/wide/deep is it', 'how tall is it', 'how big is it', 'what are its dimensions' - where the box alone shows the answer. Note 'how tall is it' asks for the part's HEIGHT, not its clearance, so it is false. Asked both at once, call twice. Default false.",
          false,
        ),
        heightOf: stringEnum(
          "WHICH line the ruler is drawn from; all three heights are returned either way. bottomline (the default) is the part's lowest point - every plain 'how high is it off the ground' question. centerline ONLY when the user asks about the part's center line, centerline or middle ('what is its centerline height', 'how high is the center of it'); topline ONLY when they ask about its top or highest point. Passing it makes the call a clearance question, so it needs no showGroundRuler. With a requirement to check, leave it out: the ruler follows requirementDatum.",
          true,
          "bottomline",
          "centerline",
          "topline",
        ),
        requirementQuote: stringParam(
          "Only to CHECK the part against a requirement: the requirement's exact words, copied from a passage a document search returned in this conversation. Verified against those passages; a quote that is not in one is rejected.",
        ),
        requirementSubject: stringParam(
          "Only with a requirement: the words IN requirementQuote that name what the requirement is about (the 'displays and touch panels' in 'the height of displays and touch panels should be...'). It must share a word with this part's name or one of its aliases, or the check is refused: a requirement about something else is not this part's requirement.",
        ),
        requirementProperty: stringEnum(
          "Required with a requirement: the dimension it is about. height_above_ground is how high the part sits (a mounting or viewing height); overall_height is how tall the part itself is; width, depth and length are the part's own size. A bare 'height' is whichever the passage means: a position in the room is height_above_ground, the size of the part is overall_height.",
          true,
          "height_above_ground",
          "overall_height",
          "width",
          "depth",
          "length",
        ),
        requirementMin: numberParam("The requirement's lower limit, exactly as the quote writes it, in requirementUnit."),
        requirementMax: numberParam("The requirement's upper limit, exactly as the quote writes it, in requirementUnit."),
        requirementNominal: numberParam("The requirement's nominal value when the quote gives one with a tolerance (the 1500 in '1500 mm ± 50 mm'), in requirementUnit."),
        requirementTolerance: numberParam("The plus-or-minus tolerance on requirementNominal (the 50 in '1500 mm ± 50 mm'), in requirementUnit."),
        requirementUnit: stringEnum(
          "The unit the quote states the requirement in. When the quote gives two units, use the one the figures you passed are written in.",
          true,
          "mm",
          "cm",
          "m",
          "in",
          "ft",
        ),
        requirementDatum: stringEnum(
          "Only for height_above_ground: the line the passage says the requirement is measured to: bottomline (the lowest point or bottom edge), centerline (the centerline or middle), topline (the top or highest point), or unspecified when the passage does not say. Never guess a line the passage does not name: unspecified makes the check test all three.",
          true,
          "bottomline",
          "centerline",
          "topline",
          "unspecified",
        ),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.ClearBoundingBox,
      category: "Context",
      description: `Removes whatever ${AgentToolNames.GetPartDimensions} drew - the bounding box or the ground ruler. Use when the user asks to clear, hide, or dismiss the box, the ruler, or the measurement.`,
      humanDescription: "Clear the measurement box or ruler from the view.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: false,
    },
  );
}
